import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Flame, Beef, Wheat, Droplets, Pill, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../lib/utils';

// Daily recommended values for reference
const DV = {
  calories: 2000,
  protein_g: 50,
  carbs_g: 300,
  fat_g: 65,
  fiber_g: 28,
  sugar_g: 50,
  sodium_mg: 2300,
  vitamin_a_mcg: 900,
  vitamin_c_mg: 90,
  vitamin_d_mcg: 20,
  vitamin_e_mg: 15,
  vitamin_k_mcg: 120,
  vitamin_b12_mcg: 2.4,
  calcium_mg: 1300,
  iron_mg: 18,
  magnesium_mg: 420,
  potassium_mg: 4700,
  zinc_mg: 11,
};

const MacroBar = ({ label, value, max, color, icon: Icon }) => {
  const percent = Math.min((value / max) * 100, 100);
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
          <span className="text-muted-foreground">{label}</span>
        </div>
        <span className="font-medium text-foreground">{value}g</span>
      </div>
      <Progress value={percent} className={cn("h-2", color)} />
      <p className="text-xs text-muted-foreground text-right">{percent.toFixed(0)}% DV</p>
    </div>
  );
};

const MicroNutrient = ({ label, value, unit, dv }) => {
  const percent = dv ? Math.min((value / dv) * 100, 100) : 0;
  
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-foreground">{value}{unit}</span>
        {dv && (
          <Badge variant={percent >= 100 ? "default" : percent >= 50 ? "secondary" : "outline"} className="text-xs">
            {percent.toFixed(0)}%
          </Badge>
        )}
      </div>
    </div>
  );
};

export const NutritionPanel = ({ dailyTotals, compact = false }) => {
  const [showMicros, setShowMicros] = useState(false);
  
  if (!dailyTotals) return null;
  
  const { calories, macros, micros } = dailyTotals;
  
  if (compact) {
    return (
      <div className="flex flex-wrap gap-3 text-sm">
        <Badge variant="outline" className="gap-1">
          <Flame className="h-3 w-3" aria-hidden="true" />
          {calories} cal
        </Badge>
        <Badge variant="outline" className="gap-1">
          <Beef className="h-3 w-3" aria-hidden="true" />
          {macros?.protein_g}g protein
        </Badge>
        <Badge variant="outline" className="gap-1">
          <Wheat className="h-3 w-3" aria-hidden="true" />
          {macros?.carbs_g}g carbs
        </Badge>
        <Badge variant="outline" className="gap-1">
          <Droplets className="h-3 w-3" aria-hidden="true" />
          {macros?.fat_g}g fat
        </Badge>
      </div>
    );
  }
  
  return (
    <Card data-testid="nutrition-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Pill className="h-5 w-5 text-primary" aria-hidden="true" />
          Daily Nutrition Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Calories */}
        <div className="text-center p-4 bg-primary/5 rounded-xl">
          <p className="text-sm text-muted-foreground">Total Calories</p>
          <p className="text-4xl font-bold text-primary">{calories}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {((calories / DV.calories) * 100).toFixed(0)}% of daily value
          </p>
        </div>
        
        {/* Macronutrients */}
        <div className="space-y-4">
          <h4 className="font-semibold text-foreground flex items-center gap-2">
            Macronutrients
          </h4>
          
          <MacroBar 
            label="Protein" 
            value={macros?.protein_g || 0} 
            max={DV.protein_g} 
            icon={Beef}
          />
          <MacroBar 
            label="Carbohydrates" 
            value={macros?.carbs_g || 0} 
            max={DV.carbs_g}
            icon={Wheat}
          />
          <MacroBar 
            label="Fat" 
            value={macros?.fat_g || 0} 
            max={DV.fat_g}
            icon={Droplets}
          />
          <MacroBar 
            label="Fiber" 
            value={macros?.fiber_g || 0} 
            max={DV.fiber_g}
          />
          <MacroBar 
            label="Sugar" 
            value={macros?.sugar_g || 0} 
            max={DV.sugar_g}
          />
        </div>
        
        {/* Micronutrients Toggle */}
        {micros && (
          <div>
            <button
              onClick={() => setShowMicros(!showMicros)}
              className="w-full flex items-center justify-between py-2 text-sm font-semibold text-foreground hover:text-primary transition-colors"
              aria-expanded={showMicros}
              data-testid="toggle-micros-btn"
            >
              <span>Micronutrients (Vitamins & Minerals)</span>
              {showMicros ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            
            {showMicros && (
              <div className="mt-2 p-4 bg-muted/30 rounded-xl space-y-1 animate-fade-in">
                <div className="grid md:grid-cols-2 gap-x-6">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Vitamins</p>
                    <MicroNutrient label="Vitamin A" value={micros.vitamin_a_mcg} unit="mcg" dv={DV.vitamin_a_mcg} />
                    <MicroNutrient label="Vitamin C" value={micros.vitamin_c_mg} unit="mg" dv={DV.vitamin_c_mg} />
                    <MicroNutrient label="Vitamin D" value={micros.vitamin_d_mcg} unit="mcg" dv={DV.vitamin_d_mcg} />
                    <MicroNutrient label="Vitamin E" value={micros.vitamin_e_mg} unit="mg" dv={DV.vitamin_e_mg} />
                    <MicroNutrient label="Vitamin K" value={micros.vitamin_k_mcg} unit="mcg" dv={DV.vitamin_k_mcg} />
                    <MicroNutrient label="Vitamin B12" value={micros.vitamin_b12_mcg} unit="mcg" dv={DV.vitamin_b12_mcg} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase mb-2">Minerals</p>
                    <MicroNutrient label="Calcium" value={micros.calcium_mg} unit="mg" dv={DV.calcium_mg} />
                    <MicroNutrient label="Iron" value={micros.iron_mg} unit="mg" dv={DV.iron_mg} />
                    <MicroNutrient label="Magnesium" value={micros.magnesium_mg} unit="mg" dv={DV.magnesium_mg} />
                    <MicroNutrient label="Potassium" value={micros.potassium_mg} unit="mg" dv={DV.potassium_mg} />
                    <MicroNutrient label="Zinc" value={micros.zinc_mg} unit="mg" dv={DV.zinc_mg} />
                    <MicroNutrient label="Sodium" value={micros.sodium_mg} unit="mg" dv={DV.sodium_mg} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const NutritionGuide = ({ guide }) => {
  if (!guide) return null;
  
  return (
    <Card data-testid="nutrition-guide">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Nutrition Guide</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground">{guide.diet_focus}</p>
        
        {/* Macro Ratio */}
        {guide.macro_ratio && (
          <div>
            <p className="text-sm font-medium text-foreground mb-2">Recommended Macro Ratio</p>
            <div className="flex gap-2">
              <Badge variant="secondary" className="flex-1 justify-center py-2">
                <Beef className="h-3 w-3 mr-1" aria-hidden="true" />
                {guide.macro_ratio.protein_percent}% Protein
              </Badge>
              <Badge variant="secondary" className="flex-1 justify-center py-2">
                <Wheat className="h-3 w-3 mr-1" aria-hidden="true" />
                {guide.macro_ratio.carbs_percent}% Carbs
              </Badge>
              <Badge variant="secondary" className="flex-1 justify-center py-2">
                <Droplets className="h-3 w-3 mr-1" aria-hidden="true" />
                {guide.macro_ratio.fat_percent}% Fat
              </Badge>
            </div>
          </div>
        )}
        
        {/* Key Nutrients */}
        {guide.key_nutrients && (
          <div>
            <p className="text-sm font-medium text-foreground mb-2">Key Nutrients to Focus On</p>
            <div className="flex flex-wrap gap-2">
              {guide.key_nutrients.map((nutrient, idx) => (
                <Badge key={idx} variant="outline">{nutrient}</Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Foods */}
        <div className="grid md:grid-cols-2 gap-4">
          {guide.foods_emphasized && (
            <div className="p-3 bg-green-500/10 rounded-xl">
              <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-2">Foods to Emphasize</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {guide.foods_emphasized.map((food, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" aria-hidden="true" />
                    {food}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {guide.foods_limited && (
            <div className="p-3 bg-red-500/10 rounded-xl">
              <p className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">Foods to Limit</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                {guide.foods_limited.map((food, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden="true" />
                    {food}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const MealNutrition = ({ nutrition }) => {
  if (!nutrition) return null;
  
  return (
    <div className="flex flex-wrap gap-2 text-xs" data-testid="meal-nutrition">
      <Badge variant="outline" className="gap-1">
        <Flame className="h-3 w-3" aria-hidden="true" />
        {nutrition.calories} cal
      </Badge>
      <Badge variant="outline" className="gap-1">
        {nutrition.protein_g}g protein
      </Badge>
      <Badge variant="outline" className="gap-1">
        {nutrition.carbs_g}g carbs
      </Badge>
      <Badge variant="outline" className="gap-1">
        {nutrition.fat_g}g fat
      </Badge>
    </div>
  );
};
